"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { GAME_URL } from "@/lib/game/tournaments";

const navItems = [
  { label: "Tournaments", href: "/tournaments" },
  { label: "Partners", href: "/partners" },
  { label: "Roadmap", href: "/roadmap" },
  { label: "About", href: "/about" },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while drawer open + close on Escape.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled || open
            ? "bg-base/80 backdrop-blur-md border-b border-line"
            : "bg-transparent"
        }`}
      >
        <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12"
        aria-label="Primary"
      >
        <Link
          href="/"
          onClick={() => setOpen(false)}
          className="flex items-center gap-3 font-display text-2xl tracking-wide text-fg"
        >
          <Image
            src="/images/traderoyale-crown-mark.png"
            alt=""
            width={32}
            height={32}
            quality={90}
            className="h-8 w-8 object-contain"
          />
          <span>TRADE ROYALE</span>
        </Link>

        <ul className="hidden items-center gap-8 lg:flex">
          {navItems.map((item) => (
            <li key={item.href}>
              <Link
                href={item.href}
                prefetch={false}
                className="font-body text-sm uppercase tracking-wider text-fg-muted transition-colors hover:text-gold"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="hidden lg:block">
          <Button
            variant="gold-filled"
            size="sm"
            href={GAME_URL}
            className="px-4 py-1.5 text-xs"
          >
            Play Free
          </Button>
        </div>

        <button
          type="button"
          className="lg:hidden text-fg p-2 -mr-2"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav-drawer"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="sr-only">Menu</span>
          {open ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
        </nav>
      </header>

      {/* Mobile drawer — RENDERED AS SIBLING OF <header>, NOT inside it.
          The header uses backdrop-blur-md when open, which creates a CSS
          containing block for any `position: fixed` descendants — that
          trapped the drawer inside the header's tiny top-bar box and
          left the hero visible through it. Sibling rendering escapes the
          containing-block trap so `fixed inset-0` resolves to the
          viewport. `bg-black` (Tailwind core, no theme indirection) is
          solid opaque and immune to any theme-utility ambiguity. z-[70]
          sits above the header's z-50 stacking context. */}
      <div
        id="mobile-nav-drawer"
        className={`lg:hidden fixed inset-0 z-[70] bg-black transition-opacity duration-200 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <div className="flex min-h-full flex-col px-6 pb-10 pt-20">
          {/* In-drawer close button (top right, mirrors header position). */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="absolute right-4 top-4 p-3 text-fg"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <ul className="flex flex-col">
            {navItems.map((item) => (
              <li key={item.href} className="border-b border-line">
                <Link
                  href={item.href}
                  prefetch={false}
                  onClick={() => setOpen(false)}
                  className="block py-5 font-display text-3xl uppercase tracking-wider text-fg transition-colors hover:text-gold"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-10" onClickCapture={() => setOpen(false)}>
            <Button
              variant="gold-filled"
              size="md"
              href={GAME_URL}
              className="w-full text-center"
            >
              Play Free
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
